/**
 * localStorage key registry + one-time migration off the legacy `mtfuel:`
 * prefix (the app's MasterTour-era name).
 *
 * Every client-side storage key is built here so the prefix lives in exactly
 * one place. `ensureStorageMigrated()` runs lazily from the key builders: the
 * first read or write of any key in a page load moves legacy `mtfuel:*`
 * entries to their new names, so saved MPG/tank presets, recent routes, and
 * imported-stop progress survive the rename. It is idempotent, never
 * overwrites a value already stored under the new key, and — like every other
 * storage access in the app — never throws (SSR, private mode, disabled
 * storage all degrade to a no-op).
 *
 * localStorage is per-origin: this only carries data forward for users on the
 * same host. A move to a new host starts with empty storage regardless.
 */

export const STORAGE_PREFIX = 'fuel-estimator:'

const LEGACY_PREFIX = 'mtfuel:'

/** Legacy sub-prefixes that were renamed, not just re-prefixed. */
const LEGACY_RENAMES: ReadonlyArray<readonly [string, string]> = [
	['mtfuel:mt-imported:', `${STORAGE_PREFIX}imported:`],
]

/** Map a legacy `mtfuel:*` key to its new name. */
export function migratedKey(legacyKey: string): string {
	for (const [from, to] of LEGACY_RENAMES) {
		if (legacyKey.startsWith(from)) return to + legacyKey.slice(from.length)
	}
	return STORAGE_PREFIX + legacyKey.slice(LEGACY_PREFIX.length)
}

/**
 * Move every legacy key in `storage` to its new name. An existing value under
 * the new key wins (the user has already used the renamed app); the legacy
 * entry is removed either way. Returns how many legacy keys were processed.
 */
export function migrateLegacyStorage(storage: Storage): number {
	const legacyKeys: string[] = []
	for (let i = 0; i < storage.length; i++) {
		const key = storage.key(i)
		if (key?.startsWith(LEGACY_PREFIX)) legacyKeys.push(key)
	}
	for (const key of legacyKeys) {
		const value = storage.getItem(key)
		const next = migratedKey(key)
		if (value !== null && storage.getItem(next) === null) storage.setItem(next, value)
		storage.removeItem(key)
	}
	return legacyKeys.length
}

let migrated = false

/** Run the legacy-key migration once per page load. Safe to call anywhere. */
export function ensureStorageMigrated(): void {
	if (migrated || typeof window === 'undefined') return
	migrated = true
	try {
		migrateLegacyStorage(window.localStorage)
	} catch {
		// Storage unavailable or over quota — legacy values simply aren't carried over.
	}
}

function key(suffix: string): string {
	ensureStorageMigrated()
	return STORAGE_PREFIX + suffix
}

export const storageKeys = {
	customMpg: (vehicleId: string) => key(`custom_mpg:${vehicleId}`),
	customTank: (vehicleId: string) => key(`custom_tank:${vehicleId}`),
	customHeight: (vehicleId: string) => key(`custom_height_m:${vehicleId}`),
	auxPowerOn: (vehicleId: string) => key(`aux_power_on:${vehicleId}`),
	customGph: (vehicleId: string) => key(`custom_gph:${vehicleId}`),
	recentRoutes: () => key('recent-routes:v1'),
	imported: (sourceKey: string) => key(`imported:${sourceKey}`),
}

/** Test-only: reset the once-per-load guard. */
export function resetStorageMigrationForTests(): void {
	migrated = false
}
